import * as React from "react";
import {
  Checkbox,
  Grid,
  InputLabel,
  Link,
  ListItemText,
  MenuItem,
  OutlinedInput,
} from "@material-ui/core";
import { Box, Avatar } from "@material-ui/core";
import { Delete } from "@material-ui/icons";
import { Add } from "@material-ui/icons";
import { Select } from "@material-ui/core";
import { AddCollaboratorDialog } from "./add-collaborator-dialog";
import SimpleTrack from "../../../../../../../../common/types/release/simple-track";
import { Collaborator } from "../../../../../../../../services/collaborator/types/collaborator";
import { ValidType } from "../../../../../../../../services/collaborator/types/valid-types";
import {
  deleteCollaborator,
  createCollaborator,
} from "../../../../../sdk/collaborators";
import { inviteArtist } from "../../../../../sdk/artists";
import { ArtistSearchResult } from "../../../../../../../../services/artist/types/artist-search-result";

interface TrackDetailCollaboratorsProps {
  track: SimpleTrack;
  collaborators: Collaborator[];
  collaboratorType: ValidType;
  roles: string[];
  onChange: (collaborators: Collaborator[]) => void;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export const TrackDetailCollaborators: React.FC<
  TrackDetailCollaboratorsProps
> = ({ collaborators, collaboratorType, ...props }) => {
  const [targetCollabs, setTargetCollabs] = React.useState<Collaborator[]>([]);

  React.useEffect(() => {
    setTargetCollabs(collaborators.filter((c) => c.type === collaboratorType));
  }, [collaborators, collaboratorType]);

  const [showAddCollaboratorMenu, setShowAddCollaboratorMenu] =
    React.useState(false);
  const removeCollaborator = (id?: string) => {
    if (id) {
      setTargetCollabs(targetCollabs.filter((c) => c.id !== id));
      deleteCollaborator(props.track.id, id).then(() => {});
    }
  };

  const addCollaboratorToTrack = (
    identity: ArtistSearchResult | string,
    role: string
  ) => {
    if (collaboratorType === "COLLABORATOR") {
      const artist = identity as ArtistSearchResult;
      inviteArtist({ name: artist.name, externalProfile: artist }).then(
        (simple_artist) => {
          createCollaborator(props.track.id, {
            name: simple_artist.name,
            collaboratorId: simple_artist.id,
            avatar: simple_artist.avatar,
            assetId: props.track.id,
            releaseId: props.track.releaseId,
            type: collaboratorType,
            assetType: "TRACK",
            role,
            collaboratorType: "ARTIST",
          }).then((collaborator) => {
            setTargetCollabs([...targetCollabs, collaborator]);
          });
        }
      );
    }

    if (collaboratorType === "CONTRIBUTOR") {
      createCollaborator(props.track.id, {
        name: identity as string,
        collaboratorId: `NAME_ONLY#${identity}`,
        assetId: props.track.id,
        releaseId: props.track.releaseId,
        type: collaboratorType,
        assetType: "TRACK",
        role,
        skipInvite: true,
        collaboratorType: "ARTIST",
      }).then((collaborator) => {
        setTargetCollabs([...targetCollabs, collaborator]);
      });
    }
  };

  const handleRoleChange = (collaborator: Collaborator, role: string[]) => {
    if (role) {
      props.onChange([
        ...targetCollabs.filter((c) => c.id !== collaborator.id),
        { ...collaborator, role: role.join(',') },
      ]);
      setTargetCollabs([
        ...targetCollabs.filter((c) => c.id !== collaborator.id),
        { ...collaborator, role: role.join(',') },
      ]);
    }
  };

  return (
    <div style={{ marginTop: "25px" }}>
      <Box display="flex" flexDirection="row" width="100%">
        <InputLabel style={{ fontSize: 24, fontWeight: 300 }}>
          {collaboratorType === "COLLABORATOR" ? "Collaborators" : "Credits"}
        </InputLabel>
        <div
          style={{ marginLeft: "auto" }}
          onClick={() => setShowAddCollaboratorMenu(true)}
        >
          <Add />
        </div>
      </Box>
      <Grid container spacing={2}>
        {targetCollabs.map((collaborator) => (
          <Grid item xs={6} md={4}>
            <Box>
              <Grid direction="row">
                <Link href={`/artists/${collaborator.collaboratorId}`}>
                  <Avatar alt={collaborator.name} src={collaborator.avatar} />
                  <div>{collaborator.name}</div>
                </Link>
                <Select
                  labelId="role_label"
                  id="collaborator_role"
                  multiple
                  value={collaborator.role.split(',')}
                  renderValue={() => collaborator.role}
                  onChange={(event) =>
                    handleRoleChange(
                      collaborator,
                      event.target.value as unknown as string[]
                    )
                  }
                  input={<OutlinedInput label="Role" />}
                  MenuProps={MenuProps}
                >
                  {props.roles.map((role) => (
                    <MenuItem key={role} value={role}>
                      <Checkbox
                        checked={collaborator.role.split(',').filter((c) => c === role).length > 0}
                      />
                      <ListItemText primary={role.split(',')} />
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <div onClick={() => removeCollaborator(collaborator.id)}>
                <Delete />
              </div>
            </Box>
          </Grid>
        ))}
      </Grid>
      <AddCollaboratorDialog
        isOpen={showAddCollaboratorMenu}
        title={collaboratorType === "COLLABORATOR" ? "Collaborator" : "Credit"}
        roles={props.roles}
        onClose={() => setShowAddCollaboratorMenu(false)}
        onAddCollaborator={addCollaboratorToTrack}
      />
    </div>
  );
};
